import {
  BarChart3,
  Calendar,
  CheckCircle2,
  ExternalLink,
  Globe,
  Lock,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useD3LineChart } from "../hooks/useD3LineChart";
import { BasketStock } from "../services/basketsService";
import {
  AccessShareableLinkResponse,
  shareableLinksService,
} from "../services/shareableLinksService";
import { trackShareableLinkVisit } from "../utils/analytics";
import { shouldHideBasketPerformanceChart } from "../utils/portfolioUtils";
import PerformanceUnavailableMessage from "../components/PerformanceUnavailableMessage";

interface PerformanceMetric {
  entityId: number;
  entityType: "PORTFOLIO" | "BASKET";
  metricName: string;
  metricValue: number;
  metricDate: string;
}

interface SharedBasketData {
  id: number;
  name: string;
  description: string | null;
  provider: { // TODO: Remove this once the new changes are rolled out everywhere and the cache is reset. 
    id: number;
    name: string;
  };
  providers: {
    id: number;
    name: string;
  };
  basketStocks: BasketStock[];
  performanceMetrics?: PerformanceMetric[];
  createdAt: string;
  updatedAt: string;
  isPrivate?: boolean;
  message?: string;
}

interface ShareBasketPageProps {
  initialResponse?: AccessShareableLinkResponse | null;
}

const ShareBasketPage: React.FC<ShareBasketPageProps> = ({ initialResponse = null }) => {
  const { url, token } = useParams<{ url?: string; token?: string }>();
  const shareToken = url ?? token;
  const navigate = useNavigate();
  const [basketData, setBasketData] = useState<SharedBasketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLogoAnimation, setShowLogoAnimation] = useState<boolean>(false);
  const [showTextLogo, setShowTextLogo] = useState<boolean>(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("1 year");
  const [userOverrodePeriod, setUserOverrodePeriod] = useState<boolean>(false);

  const applySharedBasketResponse = useCallback((response: AccessShareableLinkResponse) => {
    if (response.type !== "BASKET") {
      setBasketData(null);
      setError("This link is not for a basket");
      return;
    }

    const nextBasketData = response.resourceData as SharedBasketData;
    setError(null);
    setBasketData(nextBasketData);
    setUserOverrodePeriod(false);

    // Track the shareable link visit with UTM parameters.
    trackShareableLinkVisit(
      "BASKET",
      nextBasketData.id,
      nextBasketData.name,
      nextBasketData.provider?.name || nextBasketData.providers?.name
    );
  }, []);

  const loadSharedBasket = useCallback(async (resolvedShareToken: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await shareableLinksService.accessShareableLink(resolvedShareToken);
      applySharedBasketResponse(response);
    } catch (err) {
      setBasketData(null);
      console.error("Failed to load shared basket:", err);
      if (err instanceof Error) {
        if (err.message.includes("not found")) {
          setError("This shareable link is not found or has been removed");
        } else if (err.message.includes("inactive") || err.message.includes("expired")) {
          setError("This shareable link is no longer active");
        } else {
          setError("Failed to load the shared basket. Please try again later.");
        }
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  }, [applySharedBasketResponse]);

  useEffect(() => {
    if (initialResponse) {
      setLoading(true);
      setError(null);
      applySharedBasketResponse(initialResponse);
      setLoading(false);
      return;
    }

    if (shareToken) {
      void loadSharedBasket(shareToken);
    }
  }, [applySharedBasketResponse, initialResponse, loadSharedBasket, shareToken]);

  // Logo animation effect (same as Homepage)
  useEffect(() => {
    // Start spinning animation after a brief delay
    const startTimer = setTimeout(() => {
      setShowLogoAnimation(true);
    }, 300); // Start spinning after 300ms

    // Start text logo fade-in during the spin
    const textTimer = setTimeout(() => {
      setShowTextLogo(true);
    }, 800); // Start text fade after 800ms

    // Stop spinning animation after total duration
    const stopTimer = setTimeout(() => {
      setShowLogoAnimation(false);
    }, 2300); // 300ms delay + 2000ms animation = 2300ms total

    return () => {
      clearTimeout(startTimer);
      clearTimeout(textTimer);
      clearTimeout(stopTimer);
    };
  }, []);

  const formatDate = (dateString: string) => {
    // Support full ISO and date-only strings; build local Date for date-only to avoid UTC offset.
    const datePart = dateString.includes("T") ? dateString.split("T")[0] : dateString;
    const [yyyy, mm, dd] = datePart.split("-").map(Number);
    const date = yyyy && mm && dd ? new Date(yyyy, mm - 1, dd) : new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatWeight = (weight: number | string | undefined) => {
    if (weight === undefined || weight === null) {
      return "0.00%";
    }
    const numWeight = typeof weight === "string" ? parseFloat(weight) : weight;
    return `${numWeight.toFixed(2)}%`;
  };

  // Abbreviate long text to prevent layout push, appends ellipsis
  const abbreviateText = (text: string, maxChars = 28) => {
    if (!text) return "";
    return text.length > maxChars ? `${text.slice(0, Math.max(0, maxChars - 1))}…` : text;
  };

  const getPositionIcon = (positionType: string) => {
    return positionType === "LONG" ? (
      <TrendingUp className="w-4 h-4 text-green-600" />
    ) : (
      <TrendingDown className="w-4 h-4 text-red-600" />
    );
  };

  const getPositionColor = (positionType: string) => {
    return positionType === "LONG"
      ? "text-green-600 bg-green-50 border-green-200"
      : "text-red-600 bg-red-50 border-red-200";
  };

  // Render plain text with clickable hyperlinks for URLs
  const renderTextWithLinks = (text: string) => {
    const urlRegex = /((https?:\/\/|www\.)[^\s<>()]+[^\s.,!?;:)"'\]]?)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = urlRegex.exec(text)) !== null) {
      const start = match.index;
      const url = match[0];

      if (start > lastIndex) {
        parts.push(text.slice(lastIndex, start));
      }

      const href = url.startsWith("http") ? url : `https://${url}`;
      parts.push(
        <a
          key={`link-${start}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-teal-700 underline hover:text-teal-800"
        >
          {url}
        </a>
      );

      lastIndex = start + url.length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return <>{parts}</>;
  };

  const getSignupUrlWithUTM = () => {
    const params = new URLSearchParams();
    params.set("utm_source", "shared_link");
    params.set("utm_medium", "basket");
    if (basketData) {
      params.set("utm_campaign", basketData.provider?.name || basketData.providers?.name);
      params.set("utm_content", basketData.name);
    }
    return `/signup?${params.toString()}`;
  };

  const getLoginUrlWithUTM = () => {
    const params = new URLSearchParams();
    params.set("utm_source", "shared_link");
    params.set("utm_medium", "basket");
    if (basketData) {
      params.set("utm_campaign", basketData.provider?.name || basketData.providers?.name);
      params.set("utm_content", basketData.name);
    }
    return `/signup?${params.toString()}`;
  };

  // Get actual performance (cumulative from all factors)
  const getActualPerformance = (): number | null => {
    if (!basketData?.performanceMetrics || basketData.performanceMetrics.length === 0) {
      return null;
    }

    // Use factor metrics to compute cumulative performance
    const factorMetrics = basketData.performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return null;

    // Sort by date ascending and compute cumulative
    const sorted = [...factorMetrics].sort(
      (a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime()
    );

    let cumulative = 1;
    sorted.forEach(m => {
      cumulative *= m.metricValue;
    });

    return (cumulative - 1) * 100;
  };

  // Get backtest performance (cumulative performance before basket went live)
  const getBacktestPerformance = (): number | null => {
    if (!basketData?.createdAt || !basketData?.performanceMetrics || basketData.performanceMetrics.length === 0) {
      return null;
    }

    // Get the date string in yyyy-mm-dd format for the create date
    const createDateString = basketData.createdAt.split("T")[0];

    // Find all factor metrics before the create date
    const backtestMetrics = basketData.performanceMetrics
      .filter(m => {
        const metricDateString = m.metricDate.split("T")[0];
        return m.metricName === "factor" && metricDateString < createDateString;
      })
      .sort((a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime());

    if (backtestMetrics.length === 0) return null;

    // Compute cumulative performance for backtest period
    let cumulative = 1;
    backtestMetrics.forEach(m => {
      cumulative *= m.metricValue;
    });

    return (cumulative - 1) * 100;
  };

  // Get formatted create date for chart reference line
  const getFormattedCreateDate = (): string | null => {
    if (!basketData?.createdAt) return null;
    // Format as yyyy-mm-dd
    return basketData.createdAt.split("T")[0];
  };

  // Benchmark support will be added later when API is available

  // Helper to check if a filter has data
  const hasDataForFilter = useCallback(
    (filter: string, metrics: PerformanceMetric[]) => {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      switch (filter) {
        case "All": {
          // All filter always has data if there are any metrics
          return metrics.length > 0;
        }
        case "1 week": {
          const oneWeekAgo = new Date(now);
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          oneWeekAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneWeekAgo;
          });
        }
        case "Since inception": {
          if (!basketData?.createdAt) return metrics.length > 0;
          const inceptionDate = new Date(basketData.createdAt);
          inceptionDate.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= inceptionDate;
          });
        }
        case "Year to date": {
          const yearStart = new Date(now.getFullYear(), 0, 1);
          yearStart.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= yearStart;
          });
        }
        case "Month to date": {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          monthStart.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= monthStart;
          });
        }
        case "1 month": {
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          oneMonthAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneMonthAgo;
          });
        }
        case "6 months": {
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          sixMonthsAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= sixMonthsAgo;
          });
        }
        case "3 months": {
          const threeMonthsAgo = new Date(now);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          threeMonthsAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= threeMonthsAgo;
          });
        }
        case "1 year": {
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          oneYearAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneYearAgo;
          });
        }
        default:
          return metrics.length > 0;
      }
    },
    [basketData?.createdAt]
  );

  // Compute available filter options based on data availability
  const availableFilters = useMemo(() => {
    if (!basketData?.performanceMetrics || basketData.performanceMetrics.length === 0) {
      return ["Since inception"];
    }

    const factorMetrics = basketData.performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return ["Since inception"];

    const allFilters = [
      "1 week",
      "1 month",
      "3 months",
      "6 months",
      "Year to date",
      "1 year",
      "Since inception",
      "All",
    ];

    return allFilters.filter(filter => hasDataForFilter(filter, factorMetrics));
  }, [basketData?.performanceMetrics, hasDataForFilter]);

  // Ensure selected period is valid; auto-promote to 1Y when it becomes available until user overrides
  useEffect(() => {
    const filters = availableFilters as string[];
    if (!filters || filters.length === 0) return;
    if (!userOverrodePeriod && filters.includes("1 year") && selectedPeriod !== "1 year") {
      setSelectedPeriod("1 year");
      return;
    }
    if (!filters.includes(selectedPeriod)) {
      const preferred = [
        "1 year",
        "Year to date",
        "6 months",
        "3 months",
        "1 month",
        "1 week",
        "Since inception",
        "All",
      ];
      const next = preferred.find(f => filters.includes(f)) || filters[0];
      if (next) setSelectedPeriod(next);
    }
  }, [availableFilters, selectedPeriod, userOverrodePeriod]);

  const handleSelectPeriod = (value: string) => {
    setUserOverrodePeriod(true);
    setSelectedPeriod(value);
  };

  // Ensure prominent ordering for stats table: All, Since inception first
  const orderedFiltersForTable = useMemo(() => {
    const specials = ["All", "Since inception"];
    const presentSpecials = specials.filter(f => (availableFilters as string[]).includes(f));
    const rest = (availableFilters as string[]).filter(f => !specials.includes(f));
    return [...presentSpecials, ...rest];
  }, [availableFilters]);

  // Filter metrics by selected time period (basket ranges)
  const filterMetricsByPeriod = (metrics: PerformanceMetric[]) => {
    const now = new Date();
    // Set to start of day for consistent comparisons
    now.setHours(0, 0, 0, 0);

    switch (selectedPeriod) {
      case "All": {
        // Show all available data from the first date
        return metrics;
      }
      case "1 week": {
        const oneWeekAgo = new Date(now);
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        oneWeekAgo.setHours(0, 0, 0, 0);
        return metrics.filter(m => {
          const metricDate = new Date(m.metricDate);
          metricDate.setHours(0, 0, 0, 0);
          return metricDate >= oneWeekAgo;
        });
      }
      case "Since inception": {
        // Filter to only show data from basket creation date onwards (live data only)
        if (!basketData?.createdAt) return metrics;
        const inceptionDate = new Date(basketData.createdAt);
        inceptionDate.setHours(0, 0, 0, 0);
        return metrics.filter(m => {
          const metricDate = new Date(m.metricDate);
          metricDate.setHours(0, 0, 0, 0);
          return metricDate >= inceptionDate;
        });
      }
      case "Year to date": {
        const yearStart = new Date(now.getFullYear(), 0, 1);
        yearStart.setHours(0, 0, 0, 0);
        return metrics.filter(m => {
          const metricDate = new Date(m.metricDate);
          metricDate.setHours(0, 0, 0, 0);
          return metricDate >= yearStart;
        });
      }
      case "Month to date": {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        monthStart.setHours(0, 0, 0, 0);
        return metrics.filter(m => {
          const metricDate = new Date(m.metricDate);
          metricDate.setHours(0, 0, 0, 0);
          return metricDate >= monthStart;
        });
      }
      case "1 month": {
        const oneMonthAgo = new Date(now);
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        oneMonthAgo.setHours(0, 0, 0, 0);
        return metrics.filter(m => {
          const metricDate = new Date(m.metricDate);
          metricDate.setHours(0, 0, 0, 0);
          return metricDate >= oneMonthAgo;
        });
      }
      case "6 months": {
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        sixMonthsAgo.setHours(0, 0, 0, 0);
        return metrics.filter(m => {
          const metricDate = new Date(m.metricDate);
          metricDate.setHours(0, 0, 0, 0);
          return metricDate >= sixMonthsAgo;
        });
      }
      case "3 months": {
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        threeMonthsAgo.setHours(0, 0, 0, 0);
        return metrics.filter(m => {
          const metricDate = new Date(m.metricDate);
          metricDate.setHours(0, 0, 0, 0);
          return metricDate >= threeMonthsAgo;
        });
      }
      case "1 year": {
        const oneYearAgo = new Date(now);
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        oneYearAgo.setHours(0, 0, 0, 0);
        return metrics.filter(m => {
          const metricDate = new Date(m.metricDate);
          metricDate.setHours(0, 0, 0, 0);
          return metricDate >= oneYearAgo;
        });
      }
      default:
        return metrics;
    }
  };

  // Format performance data for chart (factor-based cumulative %)
  const getPerformanceChartData = () => {
    if (!basketData?.performanceMetrics || basketData.performanceMetrics.length === 0) {
      return [];
    }

    const factorMetrics = basketData.performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return [];

    const filtered = filterMetricsByPeriod(factorMetrics);
    if (filtered.length === 0) return [];

    const sorted = [...filtered].sort((a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime());

    // Build cumulative series and always prepend a 0% baseline at the first visible date
    let cumulative = 1;
    const cumulativePoints = sorted.map(m => {
      cumulative *= m.metricValue;
      return { date: m.metricDate.split("T")[0], value: (cumulative - 1) * 100 };
    });

    if (sorted.length > 0) {
      const firstDate = sorted[0].metricDate.split("T")[0];
      return [{ date: firstDate, value: 0 }, ...cumulativePoints];
    }

    return cumulativePoints;
  };

  // Get performance metrics for all timeframes
  const timeframePerformance = useMemo(() => {
    if (!basketData?.performanceMetrics || basketData.performanceMetrics.length === 0) return {};

    const factorMetrics = basketData.performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return {};

    const metrics: Record<string, number | null> = {};
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    availableFilters.forEach(filter => {
      let filtered: PerformanceMetric[] = [];

      switch (filter) {
        case "All":
          filtered = factorMetrics;
          break;
        case "1 week":
          const oneWeekAgo_metrics = new Date(now);
          oneWeekAgo_metrics.setDate(oneWeekAgo_metrics.getDate() - 7);
          oneWeekAgo_metrics.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneWeekAgo_metrics;
          });
          break;
        case "Since inception":
          if (basketData?.createdAt) {
            const inceptionDate = new Date(basketData.createdAt);
            inceptionDate.setHours(0, 0, 0, 0);
            filtered = factorMetrics.filter(m => {
              const metricDate = new Date(m.metricDate);
              metricDate.setHours(0, 0, 0, 0);
              return metricDate >= inceptionDate;
            });
          } else {
            filtered = factorMetrics;
          }
          break;
        case "Year to date":
          const yearStart = new Date(now.getFullYear(), 0, 1);
          yearStart.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= yearStart;
          });
          break;
        case "Month to date":
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          monthStart.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= monthStart;
          });
          break;
        case "1 month":
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          oneMonthAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneMonthAgo;
          });
          break;
        case "3 months":
          const threeMonthsAgo = new Date(now);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          threeMonthsAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= threeMonthsAgo;
          });
          break;
        case "6 months":
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          sixMonthsAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= sixMonthsAgo;
          });
          break;
        case "1 year":
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          oneYearAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneYearAgo;
          });
          break;
        default:
          filtered = factorMetrics;
      }

      if (filtered.length === 0) {
        metrics[filter] = null;
        return;
      }

      // Compute cumulative performance
      const sorted = [...filtered].sort((a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime());

      let cumulative = 1;
      sorted.forEach(m => {
        cumulative *= m.metricValue;
      });

      metrics[filter] = (cumulative - 1) * 100;
    });

    return metrics;
  }, [basketData?.performanceMetrics, availableFilters, basketData?.createdAt]);

  const actualPerformance = useMemo(() => getActualPerformance(), [basketData?.performanceMetrics]);
  const backtestPerformance = useMemo(
    () => getBacktestPerformance(),
    [basketData?.createdAt, basketData?.performanceMetrics]
  );
  const oneYearPerformance = useMemo(() => {
    if (!basketData?.performanceMetrics || basketData.performanceMetrics.length === 0) return null;
    const factorMetrics = basketData.performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return null;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const oneYearAgo = new Date(now);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    oneYearAgo.setHours(0, 0, 0, 0);

    const filtered = factorMetrics.filter(m => {
      const metricDate = new Date(m.metricDate);
      metricDate.setHours(0, 0, 0, 0);
      return metricDate >= oneYearAgo;
    });

    if (filtered.length === 0) return null;
    const sorted = [...filtered].sort((a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime());
    let cumulative = 1;
    sorted.forEach(m => {
      cumulative *= m.metricValue;
    });
    return (cumulative - 1) * 100;
  }, [basketData?.performanceMetrics]);
  const formattedCreateDate = useMemo(() => getFormattedCreateDate(), [basketData?.createdAt]);
  const performanceChartData = useMemo(
    () => getPerformanceChartData(),
    [basketData?.performanceMetrics, selectedPeriod]
  );

  // D3 Chart Configuration - ShareBasketPage uses light mode only
  const { svgRef, containerRef, tooltipRef } = useD3LineChart({
    data: performanceChartData,
    lines: [
      {
        dataKey: "value",
        stroke: "#14b8a6",
        strokeWidth: 2,
        label: "Total Return",
        showDots: false,
      },
      // Benchmark line hidden for now; keep for future quick enable
      // {
      //   dataKey: "benchmark",
      //   stroke: "#9ca3af",
      //   strokeWidth: 1.5,
      //   label: "SPX",
      //   showDots: false,
      //   strokeDasharray: "5 5",
      // },
    ],
    isDarkMode: false,
    height: 420,
    margins: { top: 8, right: 44, bottom: 24, left: 8 },
    yAxisFormatter: (value: number) => `${value}%`,
    tooltipFormatter: (value: number) => `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`,
    referencePoint: formattedCreateDate ? { x: formattedCreateDate, color: "#6B7280", radius: 5 } : undefined,
    showLegend: false,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
        <div className="text-center bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200/50">
          <div className="mb-6">
            <img src="/favicon.png" alt="Plutus" className="h-16 w-16 mx-auto animate-pulse" />
            <img src="/logo.png" alt="Plutus" className="h-8 w-auto mx-auto mt-2" />
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading basket...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200/50">
            <div className="mb-6">
              <img src="/favicon.png" alt="Plutus" className="h-16 w-16 mx-auto mb-2" />
              <img src="/logo.png" alt="Plutus" className="h-8 w-auto mx-auto" />
            </div>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto bg-red-50 border border-red-200">
              <ExternalLink className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold mb-4 text-gray-900">Basket Not Available</h1>
            <p className="mb-6 text-gray-600 leading-relaxed">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.open(getSignupUrlWithUTM(), "_blank")}
                className="w-full bg-teal-600 text-white font-medium py-3 px-6 rounded-xl hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Explore Plutus
              </button>
              <p className="text-sm text-gray-500">Discover investment strategies from top providers</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!basketData) {
    return null;
  }

  // Handle private basket
  if (basketData.isPrivate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        {/* Header with Plutus Branding */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => window.open(getSignupUrlWithUTM(), "_blank")}
                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
              >
                <img src="/favicon.png" alt="Plutus" className="h-12 w-12" />
                <img src="/logo.png" alt="Plutus" className="h-9 w-auto" />
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Hero Section */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-200/50">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto bg-gray-100 border border-gray-200">
                <Lock className="w-8 h-8 text-gray-500" />
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">{basketData.name}</h1>
              <div className="flex items-center justify-center space-x-2 text-gray-600 mb-6">
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">Shared by {basketData.provider?.name || basketData.providers?.name}</span>
              </div>
              {basketData.description && (
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-6">
                  {renderTextWithLinks(basketData.description)}
                </p>
              )}
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <p className="text-red-600 font-medium">{basketData.message}</p>
              </div>
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-center text-white shadow-xl">
                <h3 className="text-xl sm:text-2xl font-bold mb-6">Explore portfolios by {basketData.provider?.name || basketData.providers?.name}</h3>
                <button
                  onClick={() => window.open(getLoginUrlWithUTM(), "_blank")}
                  className="bg-white text-teal-700 font-bold py-3 px-8 rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Explore Portfolios
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header with Plutus Branding */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => window.open(getSignupUrlWithUTM(), "_blank")}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              <img src="/favicon.png" alt="Plutus" className="h-12 w-12" />
              <img src="/logo.png" alt="Plutus" className="h-9 w-auto" />
            </button>
            <div className="flex items-center space-x-3">
              <div
                className="px-4 py-2 rounded-full text-sm font-semibold bg-green-600 text-white border border-green-700 shadow-sm cursor-help relative group"
                title="This basket was created on Plutus and is certified to be accurate"
              >
                <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
                Verified
                {/* Tooltip on hover */}
                <div className="absolute hidden group-hover:block top-full right-0 mt-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl z-50">
                  <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                  This basket was created on Plutus and is certified to be accurate.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-6 space-y-6 overflow-x-hidden">
        {/* Hero Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-8 shadow-xl border border-gray-200/50">
          <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-3 mb-1">
            {/* Left side - Basket Info */}
            <div className="flex-1 text-center sm:text-left w-full sm:w-auto">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">{basketData.name}</h1>
              <div className="flex items-center justify-center sm:justify-start space-x-2 text-gray-600 mb-3">
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">Shared by {basketData.provider?.name || basketData.providers?.name}</span>
              </div>
            </div>

            {/* Right side - 1Y Performance Number */}
            {oneYearPerformance !== null && basketData.performanceMetrics && !shouldHideBasketPerformanceChart(basketData.id) && (
              <div className="text-center">
                <div
                  className={`text-5xl sm:text-6xl font-bold ${
                    oneYearPerformance >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {oneYearPerformance >= 0 ? "+" : ""}
                  {oneYearPerformance.toFixed(1)}%
                </div>
                <div className="text-[10px] sm:text-xs text-gray-400 font-medium mt-1">1Y Performance</div>
                <div className="text-[9px] sm:text-[10px] text-gray-400 mt-0.5">
                  as of{" "}
                  {formatDate(
                    [...basketData.performanceMetrics.filter(m => m.metricName === "factor")].sort(
                      (a, b) => new Date(b.metricDate).getTime() - new Date(a.metricDate).getTime()
                    )[0].metricDate
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Chart Section */}
          {shouldHideBasketPerformanceChart(basketData.id) ? (
            <div className="mb-2 rounded-lg py-6 px-6 bg-gray-50 border border-gray-200">
              <PerformanceUnavailableMessage variant="card" />
            </div>
          ) : actualPerformance !== null && performanceChartData.length > 1 ? (
            <div className="mb-2 rounded-lg py-0 px-0">
              <div ref={containerRef} style={{ width: "100%", height: 420, position: "relative", overflow: "visible" }}>
                <svg ref={svgRef} style={{ display: "block" }} />
                <div
                  ref={tooltipRef}
                  style={{
                    position: "absolute",
                    display: "none",
                    backgroundColor: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "0.375rem",
                    padding: "8px 12px",
                    pointerEvents: "none",
                    zIndex: 1000,
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  }}
                />
                {/* Watermark using same methodology as header (icon + wordmark, spaced) */}
                <div
                  className="absolute left-2 flex items-center gap-x-[6px] pointer-events-none select-none"
                  style={{ opacity: 0.14, bottom: 36 }}
                >
                  <img src="/favicon.png" alt="Plutus" className="h-9 w-9" />
                  <img src="/logo.png" alt="Plutus" className="h-7 w-auto" />
                </div>
              </div>
              {/* Time filters below chart (abbreviated) */}
              <div className="px-3 mt-0">
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-medium">
                  {[
                    { value: "1 week", label: "1W" },
                    { value: "1 month", label: "1M" },
                    { value: "3 months", label: "3M" },
                    { value: "6 months", label: "6M" },
                    { value: "Year to date", label: "YTD" },
                    { value: "1 year", label: "1Y" },
                    { value: "All", label: "All" },
                    { value: "Since inception", label: "Inception" },
                  ]
                    .filter(item => (availableFilters as string[]).includes(item.value))
                    .map(item => (
                      <button
                        key={item.value}
                        onClick={() => handleSelectPeriod(item.value)}
                        aria-pressed={selectedPeriod === item.value}
                        className={`px-2.5 py-1 rounded-full transition-colors border focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${
                          selectedPeriod === item.value
                            ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                            : "text-gray-600 hover:text-teal-700 hover:bg-teal-50 border-transparent"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                </div>
              </div>

              {formattedCreateDate && (
                <div className="mt-2 text-center text-[11px] text-gray-500">
                  Inception: {formatDate(formattedCreateDate)}
                </div>
              )}

              {/* Performance Stats by Timeframe */}
              <div className="mt-6 mb-4 sm:mb-6 pt-6 border-t border-gray-200 px-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {orderedFiltersForTable.map(filter => {
                    const perf = timeframePerformance[filter];
                    return (
                      <div key={filter} className="text-center">
                        <p className="text-xs text-gray-600 mb-1">{filter}</p>
                        <p
                          className={`text-base font-bold ${
                            perf !== null && perf !== undefined
                              ? perf >= 0
                                ? "text-teal-600"
                                : "text-red-600"
                              : "text-gray-400"
                          }`}
                        >
                          {perf !== null && perf !== undefined ? `${perf >= 0 ? "+" : ""}${perf.toFixed(2)}%` : "-"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}

          {/* Key Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
              <div className="text-2xl sm:text-3xl font-bold text-blue-700 mb-1">{basketData.basketStocks.length}</div>
              <div className="text-xs sm:text-sm text-blue-600 font-medium">Total Positions</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
              <div className="text-2xl sm:text-3xl font-bold text-green-700 mb-1">
                {basketData.basketStocks.filter(s => s.positionType === "LONG").length}
              </div>
              <div className="text-xs sm:text-sm text-green-600 font-medium">Long Positions</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200 col-span-2 sm:col-span-1">
              <div className="text-2xl sm:text-3xl font-bold text-red-700 mb-1">
                {basketData.basketStocks.filter(s => s.positionType === "SHORT").length}
              </div>
              <div className="text-xs sm:text-sm text-red-600 font-medium">Short Positions</div>
            </div>
          </div>

          {/* Basket Description (moved below positions tiles) */}
          {basketData.description && (
            <div
              className="mt-4 sm:mt-6 text-gray-700 text-sm sm:text-base leading-relaxed"
              title={basketData.description}
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical" as any,
                overflow: "hidden",
              }}
            >
              {renderTextWithLinks(basketData.description)}
            </div>
          )}

          {/* Meta Information */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Created {formatDate(basketData.createdAt)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4" />
                <span>Public Basket</span>
              </div>
            </div>
          </div>
        </div>

        {/* Connect Broker Section */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-center text-white shadow-xl">
          <h3 className="text-xl sm:text-2xl font-bold mb-6">Explore portfolios by {basketData.provider?.name || basketData.providers?.name}</h3>
          <button
            onClick={() => window.open(getLoginUrlWithUTM(), "_blank")}
            className="bg-white text-teal-700 font-bold py-3 px-8 rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Explore Portfolios
          </button>
        </div>

        {/* Holdings Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-teal-600" />
              Portfolio Holdings
            </h2>
            <p className="text-sm text-gray-600 mt-1">Complete breakdown of positions and weightings</p>
          </div>

          {basketData.basketStocks.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">No holdings in this basket</p>
              <p className="text-sm text-gray-500 mt-1">This basket is currently empty</p>
            </div>
          ) : (
            <div className="overflow-hidden">
              {/* Mobile-friendly cards for small screens */}
              <div className="block sm:hidden">
                <div className="divide-y divide-gray-200">
                  {basketData.basketStocks.map((stock, index) => (
                    <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{stock.ticker || `ConID: ${stock.conid}`}</div>
                          {stock.name && (
                            <div className="text-sm text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
                              {abbreviateText(stock.name, 28)}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center ml-2">
                          {getPositionIcon(stock.positionType)}
                          <span
                            className={`ml-1 text-xs font-medium ${getPositionColor(
                              stock.positionType
                            )} px-2 py-1 rounded-full border`}
                          >
                            {stock.positionType}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <div className="text-gray-600">
                          Weight: <span className="font-medium text-gray-900">{formatWeight(stock.weight)}</span>
                        </div>
                        <div className="text-gray-600">{stock.exchange || "N/A"}</div>
                      </div>
                      {stock.price && (
                        <div className="mt-1 text-sm font-medium text-gray-900">
                          {stock.currency} {stock.price.toFixed(2)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Table for larger screens */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Security
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Position
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Weight
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Exchange
                      </th>
                      {basketData.basketStocks.some(stock => stock.price) && (
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Price
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {basketData.basketStocks.map((stock, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {stock.ticker || `ConID: ${stock.conid}`}
                            </div>
                            {stock.name && (
                              <div className="text-sm text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
                                {abbreviateText(stock.name, 36)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getPositionIcon(stock.positionType)}
                            <span
                              className={`ml-2 text-sm font-medium ${getPositionColor(
                                stock.positionType
                              )} px-2 py-1 rounded-full border`}
                            >
                              {stock.positionType}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {formatWeight(stock.weight)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{stock.exchange || "N/A"}</td>
                        {basketData.basketStocks.some(s => s.price) && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {stock.price ? (
                              <div className="font-medium">
                                {stock.currency} {stock.price.toFixed(2)}
                              </div>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Connect Broker Section - Bottom */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-center text-white shadow-xl">
          <h3 className="text-xl sm:text-2xl font-bold mb-6">Explore portfolios by {basketData.provider?.name || basketData.providers?.name}</h3>
          <button
            onClick={() => window.open(getLoginUrlWithUTM(), "_blank")}
            className="bg-white text-teal-700 font-bold py-3 px-8 rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Explore Portfolios
          </button>
        </div>

        {/* Plutus Branding Footer */}
        <div className="mt-8 text-center">
          <button
            onClick={() => window.open(getSignupUrlWithUTM(), "_blank")}
            className="inline-flex items-center space-x-3 hover:opacity-80 transition-opacity group"
          >
            <img src="/favicon.png" alt="Plutus" className="h-12 w-12 group-hover:scale-105 transition-transform" />
            <img src="/logo.png" alt="Plutus" className="h-8 w-auto group-hover:scale-105 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareBasketPage;
